import React, { useMemo, useState } from "react";
import {
  Search,
  SlidersHorizontal,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ArrowUpDown,
  Eye,
  History,
  Bell,
  Pencil,
  Power,
  Car,
  CalendarDays,
  CalendarCheck,
  Fuel,
  Settings2,
  Cog,
  Palette,
  Users,
  Hash,
  ShieldCheck,
  FileText,
  Download,
  Wallet,
  Plus,
  StickyNote,
  AlertTriangle,
  ClipboardList,
  Star,
} from "lucide-react";

import "./ClientVehiclesTab.css";

const IMAGE_BASE_URL = "https://estbn.com/EstbnNew/public";

const COUNTRIES = { EG: "Egypt", SA: "Saudi Arabia", AE: "UAE", KW: "Kuwait" };

const staticVehicleData = {
  plate: "ABC-1234",
  country: "Egypt",
  status: "Active",
  fuelType: "Petrol",
  engine: "2.0L Turbo",
  transmission: "Automatic",
  color: "Black",
  driveType: "RWD",
  seats: "5",
  purchaseDate: "10 Jan 2021",
  warranty: "Active until 10 Jan 2026",
  mileage: "28,500 KM",
  lastOrderDate: "01 May 2024",
  lastOrderType: "Oil Change & Filter",
  lastOrderMileage: "28,500 KM",
  nextOrderDate: "15 Aug 2024",
  nextOrderType: "Tire Change & Balancing",
  completedOrders: "10",
  activeOrders: "1",
  cancelledOrders: "1",
  totalSpent: "24,560",
  upcomingOrders: "2",
};

const staticNotes = [
  {
    id: "n1",
    initials: "AS",
    name: "Ahmed Samir",
    role: "Admin",
    date: "01 May 2024, 10:30 AM",
    text: "Client prefers morning appointments between 9 AM – 12 PM.",
    tone: "green",
  },
  {
    id: "n2",
    initials: "MA",
    name: "Mona Adel",
    role: "Support",
    date: "25 Apr 2024, 02:15 PM",
    text: "Discussed extended warranty option.",
    tone: "orange",
  },
  {
    id: "n3",
    initials: "EN",
    name: "Engineer",
    role: "Mahmoud",
    date: "20 Apr 2024, 03:47 PM",
    text: "Front brakes need replacement. Recommended next visit.",
    tone: "blue",
  },
];

const staticUpcomingOrders = [
  {
    id: "u1",
    title: "Oil Change & Filter",
    date: "15 Aug 2024 • 10:00 AM",
    code: "#ESTBN-ORD-240815-0012",
    status: "Scheduled",
    tone: "green",
  },
  {
    id: "u2",
    title: "Tire Change & Balancing",
    date: "25 Aug 2024 • 02:00 PM",
    code: "#ESTBN-ORD-240825-0014",
    status: "Confirmed",
    tone: "orange",
  },
];

/* =========================================================
   HELPERS
   ========================================================= */

const has = (v) => v !== null && v !== undefined && v !== "";

const fileUrl = (path) => {
  if (!path) return null;
  return path.startsWith("http") ? path : `${IMAGE_BASE_URL}${path}`;
};

const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (isNaN(x.getTime())) return "—";
  return x.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getProp = (vehicle, slug) => {
  const found = vehicle?.properties?.find((p) => p?.property?.slug === slug);
  return found?.value_name || found?.value?.value || null;
};

const getExt = (path) => (path?.split(".").pop() || "file").toLowerCase();

const getPrimaryPlate = (vehicle) =>
  vehicle?.plates?.find((p) => p.is_primary) || vehicle?.plates?.[0];

const buildDocuments = (vehicle) => {
  const docs = [];

  (vehicle?.insurance_policies || []).forEach((p) => {
    if (p.document_file_path) {
      docs.push({
        id: `ins-${p.id}`,
        name: "Insurance Policy.pdf",
        url: fileUrl(p.document_file_path),
        date: p.created_at,
        ext: "pdf",
      });
    }
  });

  (vehicle?.licenses || []).forEach((l) => {
    const img = l.images?.find((i) => i.is_primary) || l.images?.[0];
    if (img?.image_url) {
      const ext = getExt(img.image_url);
      docs.push({
        id: `lic-${l.id}`,
        name: `Registration Card.${ext}`,
        url: fileUrl(img.image_url),
        date: img.created_at || l.created_at,
        ext,
      });
    }
  });

  (vehicle?.plates || []).forEach((p) => {
    const img = p.images?.[0];
    if (img?.image_url) {
      const ext = getExt(img.image_url);
      docs.push({
        id: `plt-${p.id}`,
        name: `Plate Photo.${ext}`,
        url: fileUrl(img.image_url),
        date: img.created_at || p.created_at,
        ext,
      });
    }
  });

  return docs;
};

/* =========================================================
   SMALL COMPONENTS
   ========================================================= */

const StaticDot = ({ title }) => (
  <span
    className="cv-static-dot"
    title={title || "Static data — waiting for API support"}
  />
);

/* shows the real value, or the static fallback + grey dot */
const Val = ({ value, fallback }) =>
  has(value) ? (
    <>{value}</>
  ) : (
    <>
      {fallback}
      <StaticDot />
    </>
  );

const VehicleImage = ({ vehicle, large = false }) => {
  const [idx, setIdx] = useState(0);

  const candidates = useMemo(() => {
    const imgs = vehicle?.images || [];
    const photo =
      imgs.find((i) => i.is_primary && i.image_type === "Vehicle_Profile_Photo") ||
      imgs.find((i) => i.image_type === "Vehicle_Profile_Photo") ||
      imgs[0];

    if (!photo) return [];
    return [fileUrl(photo.image_url), photo.full_image_url].filter(Boolean);
  }, [vehicle]);

  const src = candidates[idx];

  if (src) {
    return (
      <img
        className={
          large
            ? "cv-vehicle-image cv-vehicle-image--large"
            : "cv-vehicle-image"
        }
        src={src}
        alt={vehicle?.name || "Vehicle"}
        onError={() => setIdx((i) => i + 1)}
      />
    );
  }

  return (
    <div
      className={
        large
          ? "cv-vehicle-image cv-vehicle-image--large cv-vehicle-image--placeholder"
          : "cv-vehicle-image cv-vehicle-image--placeholder"
      }
    >
      <Car size={large ? 42 : 26} strokeWidth={1.5} />
    </div>
  );
};

const SelectField = ({ label, value, onChange, options }) => (
  <div className="cv-select">
    <span className="cv-select-label">{label}</span>
    <select value={value} onChange={onChange}>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <ChevronDown size={13} className="cv-select-chevron" />
  </div>
);

const DocItem = ({ doc }) => (
  <div className="cv-document">
    <FileText size={15} className={`cv-doc-icon cv-doc-icon--${doc.ext}`} />

    <div>
      <strong title={doc.name}>{doc.name}</strong>
      <span>Uploaded on {fmtDate(doc.date)}</span>
    </div>

    <a href={doc.url} target="_blank" rel="noreferrer" download title="Download">
      <Download size={13} />
    </a>
  </div>
);

/* =========================================================
   VEHICLE ROW
   ========================================================= */

function VehicleRow({ vehicle, selected, onSelect }) { 

const formatOrderDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

  const CategoryIcons = ({ categories = [] }) => {
  const MAX = 3;
  const visible = categories.slice(0, MAX);
  const extra = categories.length - MAX;

  if (!categories.length) return <span>—</span>;

  return (
    <div className="cv-order-icons">
{visible.map((cat, i) => (
  <img key={cat.id ?? i} src={`${IMAGE_BASE_URL}${cat.icon_url}`} alt={cat.name || "service"} title={cat.name} className="cv-order-icon" />
))}
{extra > 0 && <span className="cv-order-icon-more">+{extra}</span>}
    </div>
  );
};

  const [open, setOpen] = useState(selected);

  const plate = getPrimaryPlate(vehicle);
  const plateText = plate?.display_text;
  const country = plate ? COUNTRIES[plate.country_code] || plate.country_code : null;

  const fuel = getProp(vehicle, "engine-types");
  const engine = getProp(vehicle, "engine-sizes");
  const transmission = getProp(vehicle, "transmission-types");

  const mileage = has(vehicle.odometer_reading)
    ? `${Number(vehicle.odometer_reading).toLocaleString()} KM`
    : null;

  return (
    <div className={`cv-vehicle-card ${selected ? "is-selected" : ""}`}>
      <div className="cv-vehicle-row">
        {/* Vehicle */}
        <div className="cv-vehicle-main" onClick={onSelect}>
          <VehicleImage key={vehicle.id} vehicle={vehicle} />

          <div className="cv-vehicle-name">
            <strong title={vehicle.name}>{vehicle.name || "—"}</strong>
            <span>
              VIN: <Val value={vehicle.vin} fallback="Not available" />
            </span>
          </div>
        </div>

        {/* License */}
        <div className="cv-table-cell">
          <strong className="cv-plate-chip" dir="auto">
            <Val value={plateText} fallback={staticVehicleData.plate} />
          </strong>
          <span>
            <Val value={country} fallback={staticVehicleData.country} />
          </span>
        </div>

        {/* Status */}
        <div className="cv-table-cell">
          <span className="cv-status">
            <span className="cv-status-dot" />
            {staticVehicleData.status}
            <StaticDot />
          </span>
        </div>

        {/* Primary */}
        <div className="cv-table-cell cv-primary-cell">
          {vehicle.is_primary ? (
            <span className="cv-primary-badge">
              <Star size={13} fill="currentColor" />
            </span>
          ) : (
            <span className="cv-empty-primary">
              <Star size={15} />
            </span>
          )}
        </div>

        {/* Mileage */}
        <div className="cv-table-cell">
          <strong>
            <Val value={mileage} fallback={staticVehicleData.mileage} />
          </strong>
        </div>

        {/* Last order */}
        <div className="cv-table-cell">
          <strong>{formatOrderDate(vehicle.last_order?.completed_at)}</strong>
          <CategoryIcons categories={vehicle.last_order?.categories} />
        </div>

        {/* Next order */}
        <div className="cv-table-cell">
          <strong>
            {formatOrderDate(
              vehicle.next_order?.scheduled_date ||
                vehicle.next_order?.ordered_at,
            )}
          </strong>
          <CategoryIcons categories={vehicle.next_order?.categories} />
        </div>

        {/* Actions */}
        <div className="cv-actions-cell">
          <button
            className="cv-icon-btn"
            title="View vehicle"
            onClick={onSelect}
          >
            <Eye size={13} />
          </button>

          <button className="cv-icon-btn" title="More">
            <MoreVertical size={13} />
          </button>

          <button
            className="cv-expand-btn"
            onClick={() => setOpen((p) => !p)}
            title={open ? "Collapse" : "Expand"}
          >
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="cv-vehicle-details">
          <div className="cv-info-grid">
            {/* Vehicle info */}
            <div className="cv-info-list">
              <div className="cv-info-item">
                <CalendarDays size={13} />
                <span>Year</span>
                <strong>
                  <Val value={vehicle.year} fallback="—" />
                </strong>
              </div>

              <div className="cv-info-item">
                <Palette size={13} />
                <span>Color</span>
                <strong>
                  {staticVehicleData.color}
                  <StaticDot />
                </strong>
              </div>

              <div className="cv-info-item">
                <Fuel size={13} />
                <span>Fuel Type</span>
                <strong>
                  <Val value={fuel} fallback={staticVehicleData.fuelType} />
                </strong>
              </div>

              <div className="cv-info-item">
                <Cog size={13} />
                <span>Engine</span>
                <strong>
                  <Val value={engine} fallback={staticVehicleData.engine} />
                </strong>
              </div>

              <div className="cv-info-item">
                <Settings2 size={13} />
                <span>Transmission</span>
                <strong>
                  <Val
                    value={transmission}
                    fallback={staticVehicleData.transmission}
                  />
                </strong>
              </div>

              <div className="cv-info-item">
                <Car size={13} />
                <span>Drive Type</span>
                <strong>
                  {staticVehicleData.driveType}
                  <StaticDot />
                </strong>
              </div>

              <div className="cv-info-item">
                <Users size={13} />
                <span>Seats</span>
                <strong>
                  {staticVehicleData.seats}
                  <StaticDot />
                </strong>
              </div>

              <div className="cv-info-item">
                <Hash size={13} />
                <span>VIN</span>
                <strong title={vehicle.vin}>
                  <Val value={vehicle.vin} fallback="Not available" />
                </strong>
              </div>

              <div className="cv-info-item">
                <CalendarDays size={13} />
                <span>Purchase Date</span>
                <strong>
                  {staticVehicleData.purchaseDate}
                  <StaticDot />
                </strong>
              </div>

              <div className="cv-info-item">
                <ShieldCheck size={13} />
                <span>Warranty</span>
                <strong>
                  {staticVehicleData.warranty}
                  <StaticDot />
                </strong>
              </div>
            </div>

            {/* Orders summary */}
            <div className="cv-service-card">
              <div className="cv-service-header">
                <h3>Orders Summary</h3>
                <button type="button" className="cv-service-history-btn">
                  View All Orders
                </button>
              </div>

              <div className="cv-service-main">
                <div className="cv-service-row">
                  <span className="cv-service-label">Last Order</span>
                  <div className="cv-service-value-group">
                    <strong className="cv-service-value">
                      {formatOrderDate(vehicle.last_order?.completed_at)}
                    </strong>
                    <CategoryIcons
                      categories={vehicle.last_order?.categories}
                    />
                  </div>
                </div>

                <div className="cv-service-row">
                  <span className="cv-service-label">
                    Mileage at Last Order
                  </span>
                  <strong className="cv-service-value">
                    {staticVehicleData.lastOrderMileage}
                    <StaticDot />
                  </strong>
                </div>

                <div className="cv-service-row">
                  <span className="cv-service-label">Next Order</span>
                  <div className="cv-service-value-group">
                    <strong className="cv-service-value">
                      {formatOrderDate(
                        vehicle.next_order?.scheduled_date ||
                          vehicle.next_order?.ordered_at,
                      )}
                    </strong>
                    <CategoryIcons
                      categories={vehicle.next_order?.categories}
                    />
                  </div>
                </div>
              </div>

              <div className="cv-service-divider" />

              <div className="cv-service-main">
                <div className="cv-service-row">
                  <span className="cv-service-label">Total Orders</span>
                  <strong className="cv-service-value">
                    {vehicle.orders_count ?? 0}
                  </strong>
                </div>

                <div className="cv-service-row">
                  <span className="cv-service-label">Completed Orders</span>
                  <strong className="cv-service-value">
                    {vehicle.completed_orders_count}
                  </strong>
                </div>

                <div className="cv-service-row">
                  <span className="cv-service-label">Active Orders</span>
                  <strong className="cv-service-value">
                    {vehicle.active_orders_count}
                  </strong>
                </div>

                <div className="cv-service-row">
                  <span className="cv-service-label">Cancelled Orders</span>
                  <strong className="cv-service-value">
                    {vehicle.cancelled_orders_count}
                  </strong>
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="cv-quick-actions">
              <h3>Quick Actions</h3>

              <button>
                <Eye size={13} />
                View Vehicle Details
              </button>

              <button>
                <History size={13} />
                Order History
              </button>

              <button>
                <Bell size={13} />
                Add Maintenance Reminder
              </button>

              <button>
                <Pencil size={13} />
                Edit Vehicle
              </button>

              <button className="cv-danger-btn">
                <Power size={13} />
                Deactivate Vehicle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function ClientVehiclesTab({
  client,
  vehicles = [],
  loading,
  error,
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [fuel, setFuel] = useState("All");
  const [engine, setEngine] = useState("All");
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [sideTab, setSideTab] = useState("notes");

  const fuelOptions = useMemo(
    () => [
      "All",
      ...new Set(vehicles.map((v) => getProp(v, "engine-types")).filter(Boolean)),
    ],
    [vehicles]
  );

  const engineOptions = useMemo(
    () => [
      "All",
      ...new Set(vehicles.map((v) => getProp(v, "engine-sizes")).filter(Boolean)),
    ],
    [vehicles]
  );

  const filteredVehicles = useMemo(() => {
    const searchValue = search.toLowerCase();

    return vehicles.filter((vehicle) => {
      const plateText = getPrimaryPlate(vehicle)?.display_text || "";

      const matchesSearch =
        !search ||
        vehicle.name?.toLowerCase().includes(searchValue) ||
        vehicle.vin?.toLowerCase().includes(searchValue) ||
        vehicle.brand?.name?.toLowerCase().includes(searchValue) ||
        plateText.toLowerCase().includes(searchValue);

      // status is static for now (not in API)
      const matchesStatus =
        status === "All" || staticVehicleData.status === status;

      const matchesFuel = fuel === "All" || getProp(vehicle, "engine-types") === fuel;

      const matchesEngine =
        engine === "All" || getProp(vehicle, "engine-sizes") === engine;

      return matchesSearch && matchesStatus && matchesFuel && matchesEngine;
    });
  }, [vehicles, search, status, fuel, engine]);

  const selectedVehicle =
    vehicles.find((v) => v.id === selectedVehicleId) ||
    vehicles.find((v) => v.is_primary) ||
    vehicles[0];

  const selectedDocs = useMemo(
    () => (selectedVehicle ? buildDocuments(selectedVehicle) : []),
    [selectedVehicle]
  );

  if (loading) {
    return (
      <div className="cv-page">
        <div className="cv-loading">
          <div className="cv-spinner" />
          <span>Loading vehicles...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cv-page">
        <div className="cv-error">
          <AlertTriangle size={30} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const sPlate = selectedVehicle ? getPrimaryPlate(selectedVehicle) : null;
  const sCountry = sPlate
    ? COUNTRIES[sPlate.country_code] || sPlate.country_code
    : null;
  const sMileage =
    selectedVehicle && has(selectedVehicle.odometer_reading)
      ? `${Number(selectedVehicle.odometer_reading).toLocaleString()} KM`
      : null;

const apiNotes = Array.isArray(selectedVehicle?.notes)
  ? selectedVehicle.notes
  : [];

const realNotes = apiNotes.map((n, i) => {
  const typeLabel =
    typeof n.note_type === "object" && n.note_type !== null
      ? n.note_type.title
      : n.note_type;

  const name = n.title || typeLabel || "Vehicle Note";

  return {
    id: n.id ?? `real-${i}`,
    initials: String(name).trim().charAt(0).toUpperCase() || "N",
    name,
    role: n.title && typeLabel ? typeLabel : "",
    date: fmtDate(n.created_at),
    text: n.description || "",
    tone: ["green", "orange", "blue"][i % 3],
  };
});

const notesToShow = realNotes.length ? realNotes : staticNotes;
const hasRealNotes = realNotes.length > 0;

  return (
    <div className="cv-page">
      <div className="cv-canvas">
        <div className="cv-layout">
          {/* LEFT */}
          <section className="cv-main">
            {/* Filters */}
            <div className="cv-toolbar">
              <div className="cv-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <SelectField
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                options={["All", "Active", "Inactive"]}
              />

              <SelectField
                label="Fuel Type"
                value={fuel}
                onChange={(e) => setFuel(e.target.value)}
                options={fuelOptions}
              />

              <SelectField
                label="Engine Type"
                value={engine}
                onChange={(e) => setEngine(e.target.value)}
                options={engineOptions}
              />

              <button className="cv-more-filters">
                <SlidersHorizontal size={13} />
                More Filters
              </button>

              <button
                className="cv-more-filters cv-more-filters--icon"
                title="More"
              >
                <MoreVertical size={14} />
              </button>
            </div>

            {/* Table */}
            <div className="cv-table">
              <div className="cv-table-header">
                <span>Vehicle</span>
                <span>License Plate</span>
                <span>Status</span>
                <span>Primary</span>
                <span>Mileage</span>
                <span>Last Order</span>
                <span>Next Order</span>
                <span>Actions</span>
              </div>

              <div className="cv-vehicles-list">
                {filteredVehicles.length === 0 ? (
                  <div className="cv-empty">
                    <Car size={38} />
                    <h3>No vehicles found</h3>
                    <p>
                      This client doesn't have any vehicles matching your
                      filters.
                    </p>
                  </div>
                ) : (
                  filteredVehicles.map((vehicle) => (
                    <VehicleRow
                      key={vehicle.id}
                      vehicle={vehicle}
                      selected={selectedVehicle?.id === vehicle.id}
                      onSelect={() => setSelectedVehicleId(vehicle.id)}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="cv-footer">
              <span>
                Showing {filteredVehicles.length ? 1 : 0} to{" "}
                {filteredVehicles.length} of {vehicles.length} vehicles
              </span>

              <div className="cv-pagination">
                <button disabled>‹</button>
                <button className="is-active">1</button>
                <button disabled>›</button>

                <select defaultValue="10">
                  <option value="10">10 / page</option>
                </select>
              </div>
            </div>
          </section>

          {/* RIGHT */}
          <aside className="cv-sidebar">
            {selectedVehicle ? (
              <>
                <div className="cv-sidebar-card">
                  <div className="cv-sidebar-image-wrap">
                    <VehicleImage
                      key={selectedVehicle.id}
                      vehicle={selectedVehicle}
                      large
                    />

                    {selectedVehicle.is_primary && (
                      <span className="cv-primary-label">Primary</span>
                    )}
                  </div>

                  <h2>{selectedVehicle.name || "Vehicle"}</h2>

                  <div className="cv-sidebar-meta">
                    <strong dir="auto">
                      <Val
                        value={sPlate?.display_text}
                        fallback={staticVehicleData.plate}
                      />
                    </strong>
                    <span>
                      <Val
                        value={sCountry}
                        fallback={staticVehicleData.country}
                      />
                    </span>
                  </div>

                  <div className="cv-sidebar-tabs">
                    {[
                      { key: "details", label: "Details", icon: Car },
                      { key: "orders", label: "Orders", icon: CalendarCheck },
                      { key: "documents", label: "Documents", icon: FileText },
                      { key: "notes", label: "Notes", icon: StickyNote },
                    ].map(({ key, label, icon: Icon }) => (
                      <button
                        key={key}
                        type="button"
                        className={sideTab === key ? "is-active" : ""}
                        onClick={() => setSideTab(key)}
                      >
                        <Icon size={13} />
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* DETAILS TAB */}
                  {sideTab === "details" && (
                    <div className="cv-summary">
                      <h3>Vehicle Summary</h3>

                      <div className="cv-summary-row">
                        <span>Mileage</span>
                        <strong>
                          <Val
                            value={sMileage}
                            fallback={staticVehicleData.mileage}
                          />
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Fuel Type</span>
                        <strong>
                          <Val
                            value={getProp(selectedVehicle, "engine-types")}
                            fallback={staticVehicleData.fuelType}
                          />
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Engine</span>
                        <strong>
                          <Val
                            value={getProp(selectedVehicle, "engine-sizes")}
                            fallback={staticVehicleData.engine}
                          />
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Transmission</span>
                        <strong>
                          <Val
                            value={getProp(
                              selectedVehicle,
                              "transmission-types",
                            )}
                            fallback={staticVehicleData.transmission}
                          />
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Color</span>
                        <strong>
                          {staticVehicleData.color}
                          <StaticDot />
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>VIN</span>
                        <strong>
                          <Val
                            value={selectedVehicle.vin}
                            fallback="Not available"
                          />
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Purchase Date</span>
                        <strong>
                          {staticVehicleData.purchaseDate}
                          <StaticDot />
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Warranty</span>
                        <strong className="cv-green-text">
                          {staticVehicleData.warranty}
                          <StaticDot />
                        </strong>
                      </div>
                    </div>
                  )}

                  {/* ORDERS TAB */}
                  {sideTab === "orders" && (
                    <div className="cv-summary">
                      <h3>Orders</h3>

                      <div className="cv-summary-row">
                        <span>Total Orders</span>
                        <strong>{selectedVehicle.orders_count ?? 0}</strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Completed</span>
                        <strong>{selectedVehicle.completed_orders_count ?? 0}</strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Active</span>
                        <strong>
                          {selectedVehicle.active_orders_count ?? 0}
                        </strong>
                      </div>

                      <div className="cv-summary-row">
                        <span>Cancelled</span>
                        <strong>
                          {selectedVehicle.cancelled_orders_count ?? 0}
                        </strong>
                      </div>
                    </div>
                  )}

                  {/* DOCUMENTS TAB */}
                  {sideTab === "documents" && (
                    <div className="cv-summary">
                      <h3>Documents ({selectedDocs.length})</h3>

                      {selectedDocs.length === 0 ? (
                        <p className="cv-side-hint">No documents uploaded.</p>
                      ) : (
                        selectedDocs.map((doc) => (
                          <DocItem key={doc.id} doc={doc} />
                        ))
                      )}
                    </div>
                  )}

                  {/* NOTES TAB */}
                  {sideTab === "notes" && (
                    <div className="cv-notes">
                      <div className="cv-notes-header">
                        <h3>
                          Vehicle Notes
                          {!hasRealNotes && <StaticDot />}
                        </h3>
                        <button type="button">View All</button>
                      </div>

                      {notesToShow.map((note) => (
                        <div className="cv-note" key={note.id}>
                          <span
                            className={`cv-note-avatar cv-note-avatar--${note.tone}`}
                          >
                            {note.initials}
                          </span>

                          <div className="cv-note-body">
                            <strong>
                              {note.name}
                              {note.role && <em> ({note.role})</em>}
                            </strong>
                            <small>{note.date}</small>
                            <p>{note.text}</p>
                          </div>

                          <button type="button" className="cv-note-more">
                            <MoreVertical size={12} />
                          </button>
                        </div>
                      ))}

                      <button type="button" className="cv-notes-all">
                        View All Notes
                      </button>
                    </div>
                  )}
                </div>

                {/* Upcoming Orders */}
                <div className="cv-side-section">
                  <div className="cv-side-section-header">
                    <h3>
                      Upcoming Orders
                      <StaticDot />
                    </h3>
                    <button type="button">View All</button>
                  </div>

                  {staticUpcomingOrders.map((o) => (
                    <div className="cv-upcoming-item" key={o.id}>
                      <div
                        className={`cv-upcoming-icon cv-upcoming-icon--${o.tone}`}
                      >
                        <CalendarCheck size={13} />
                      </div>

                      <div>
                        <strong>{o.title}</strong>
                        <span>{o.date}</span>
                        <span>
                          {o.code}{" "}
                          <em
                            className={`cv-mini-badge cv-mini-badge--${o.tone}`}
                          >
                            {o.status}
                          </em>
                        </span>
                      </div>

                      <button type="button">View Order</button>
                    </div>
                  ))}
                </div>

                {/* Documents */}
                <div className="cv-documents">
                  <div className="cv-documents-header">
                    <h3>Vehicle Documents ({selectedDocs.length})</h3>
                    <button
                      type="button"
                      onClick={() => setSideTab("documents")}
                    >
                      View All
                    </button>
                  </div>

                  {selectedDocs.length === 0 ? (
                    <p className="cv-side-hint">No documents uploaded.</p>
                  ) : (
                    selectedDocs.map((doc) => (
                      <DocItem key={doc.id} doc={doc} />
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="cv-sidebar-empty">
                <Car size={38} />
                <p>Select a vehicle</p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
